import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { API } from '../services/api';
import './WeeklySales.css';

function WeeklySales() {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedYears, setSelectedYears] = useState([]);
  const [stores, setStores] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  // Load stores and years lists
  useEffect(() => {
    // Load stores
    API.getWeeklySalesStores()
      .then(storeList => {
        setStores(storeList);
        // Default to first store if available
        if (storeList.length > 0 && !selectedStore) {
          setSelectedStore(storeList[0]);
        }
      })
      .catch(err => {
        console.error('Error loading weekly sales stores:', err);
        // Fallback to regular stores endpoint
        API.getStores()
          .then(storeList => {
            setStores(storeList);
            if (storeList.length > 0 && !selectedStore) {
              setSelectedStore(storeList[0]);
            }
          })
          .catch(err2 => console.error('Error loading stores:', err2));
      });

    // Load available years
    API.getWeeklySalesYears()
      .then(yearList => {
        setAvailableYears(yearList);
        // Default to all years if available
        if (yearList.length > 0 && selectedYears.length === 0) {
          setSelectedYears(yearList);
        }
      })
      .catch(err => console.error('Error loading weekly sales years:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load weekly sales data when store or years change
  useEffect(() => {
    if (!selectedStore || selectedYears.length === 0) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    API.getWeeklySales(selectedStore, selectedYears)
      .then(data => {
        setSalesData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading weekly sales:', err);
        setLoading(false);
      });
  }, [selectedStore, selectedYears]);

  // Process data for chart - group by year and week
  // Matches the structure from generate_visualizations.py:
  // one line per year, all years overlaid on a single chart.
  const chartData = useMemo(() => {
    if (!salesData || salesData.length === 0) return [];

    // Group by year and week_number
    const yearWeekMap = new Map();
    
    salesData.forEach(record => {
      const year = record.year;
      const weekNumber = record.week_number;
      const key = `${year}-${weekNumber}`;
      const amount = parseFloat(record.total_amount) || 0;
      
      if (!yearWeekMap.has(key)) {
        yearWeekMap.set(key, {
          week_number: weekNumber,
          year: year,
          business_date: record.business_date,
        });
      }

      // Store amount for this year; key shape: year_<year>
      const row = yearWeekMap.get(key);
      const yearKey = `year_${year}`;
      row[yearKey] = amount;
    });

    // Sort by year, then week_number
    const data = Array.from(yearWeekMap.values())
      .sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.week_number - b.week_number;
      });

    return data;
  }, [salesData]);

  // Get unique years from selected years (filtered by what's in the data)
  const years = useMemo(() => {
    const yearSet = new Set();
    salesData.forEach(record => {
      if (record.year && selectedYears.includes(record.year)) {
        yearSet.add(record.year);
      }
    });
    return Array.from(yearSet).sort();
  }, [salesData, selectedYears]);

  // Color palette for different years
  const colors = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
  ];

  // Handle year selection toggle
  const toggleYear = (year) => {
    setSelectedYears(prev => {
      if (prev.includes(year)) {
        // Remove year if already selected (but keep at least one)
        if (prev.length > 1) {
          return prev.filter(y => y !== year);
        }
        return prev; // Keep at least one year selected
      } else {
        // Add year
        return [...prev, year].sort();
      }
    });
  };

  // Select all years
  const selectAllYears = () => {
    setSelectedYears([...availableYears]);
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Custom tooltip that shows amount per year
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">Week {label}</p>
          {payload.map((entry, index) => {
            const [, year] = entry.dataKey.split('_'); // dataKey: year_2024
            const labelText = `Year ${year}`;
            const valueText = formatCurrency(entry.value);
            return (
              <p key={index} style={{ color: entry.color }}>
                {labelText}: {valueText}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  if (loading && salesData.length === 0) {
    return <div className="component-loading">Loading weekly sales data...</div>;
  }

  if (salesData.length === 0 && !loading) {
    return (
      <div className="weekly-sales">
        <div className="sales-header">
          <h3>Weekly Sales Trends</h3>
          <div className="sales-controls">
            <label>
              Store:
              <select
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value)}
                className="store-selector"
              >
                <option value="">Select a store</option>
                {stores.map(store => (
                  <option key={store} value={store}>{store}</option>
                ))}
              </select>
            </label>
            <div className="year-selection">
              <label>Years:</label>
              <div className="year-checkboxes">
                <button 
                  type="button"
                  onClick={selectAllYears}
                  className="select-all-years"
                >
                  Select All
                </button>
                {availableYears.map(year => (
                  <label key={year} className="year-checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedYears.includes(year)}
                      onChange={() => toggleYear(year)}
                      className="year-checkbox"
                    />
                    {year}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="no-data">No weekly sales data available for the selected store and years</div>
      </div>
    );
  }

  return (
    <div className="weekly-sales">
      <div className="sales-header">
        <h3>Weekly Sales Trends</h3>
        <div className="sales-controls">
          <label>
            Store:
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="store-selector"
            >
              {stores.map(store => (
                <option key={store} value={store}>{store}</option>
              ))}
            </select>
          </label>
          <div className="year-selection">
            <label>Years:</label>
            <div className="year-checkboxes">
              {availableYears.map(year => (
                <label key={year} className="year-checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedYears.includes(year)}
                    onChange={() => toggleYear(year)}
                    className="year-checkbox"
                  />
                  {year}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="sales-charts">
        <div className="chart-container">
          <h4>
            {selectedStore.toUpperCase()} - Weekly Total Sales Amount (Excluding Catering)
          </h4>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart 
              data={chartData} 
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis 
                dataKey="week_number"
                label={{ value: 'Week Number', position: 'insideBottom', offset: -5 }}
                tickFormatter={(value) => `W${value}`}
              />
              <YAxis 
                label={{ value: 'Total Amount ($)', angle: -90, position: 'insideLeft' }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {years.map((year, index) => (
                <Line
                  key={year}
                  type="monotone"
                  dataKey={`year_${year}`}
                  name={`Year ${year}`}
                  stroke={colors[index % colors.length]}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default WeeklySales;
